import { useQuery } from '@tanstack/react-query';
import { getRolesWithStaff } from '../../roles/services';
import type { Role, Staff } from '../../types';

// Single shared cache entry for `/crm/roles-with-staff` (10 min stale, 1 h gc) —
// `useStaffList` and `useRolesWithStaff` use this same key + queryFn, different shapes via `select`.
export const ROLES_WITH_STAFF_QUERY_KEY = ['roles-with-staff'] as const;
export const ROLES_WITH_STAFF_STALE_TIME = 10 * 60 * 1000;
export const ROLES_WITH_STAFF_GC_TIME = 60 * 60 * 1000;

/** @deprecated Use `ROLES_WITH_STAFF_QUERY_KEY` directly. */
export const STAFF_LIST_QUERY_KEY = ROLES_WITH_STAFF_QUERY_KEY;

function flattenAndDedupeStaff(roles: Role[]): Staff[] {
  const byId = new Map<number, Staff>();
  for (const role of roles) {
    for (const staff of role.staffs ?? []) {
      if (!byId.has(staff.id)) byId.set(staff.id, staff);
    }
  }
  return Array.from(byId.values());
}

/**
 * Flattened, deduplicated list of staff across all roles.
 *
 * Shares its cache entry with `useRolesWithStaff()` — different shape,
 * same fetch. Calling both hooks in the same render still results in
 * exactly one network request.
 *
 * @param enabled - Whether to enable the query (useful for conditional fetching).
 */
export const useStaffList = (enabled = true) =>
  useQuery<Role[], Error, Staff[]>({
    queryKey: ROLES_WITH_STAFF_QUERY_KEY,
    queryFn: getRolesWithStaff,
    enabled,
    staleTime: ROLES_WITH_STAFF_STALE_TIME,
    gcTime: ROLES_WITH_STAFF_GC_TIME,
    select: flattenAndDedupeStaff,
  });
